export interface Location {
  /**
   * Location ID.
   * @format GUID
   * @readonly
   */
  id?: string | null;
  /**
   * Location name.
   * @maxLength 150
   */
  name?: string;
  /**
   * Location description.
   * @maxLength 500
   */
  description?: string | null;
  /**
   * Whether this is the default location. There can only be one default location per site. The default location can't be archived.
   * @readonly
   */
  default?: boolean;
  /**
   * Location status. Defaults to `ACTIVE`.
   * __Notes:__
   * - [Archiving a location](https://dev.wix.com/api/rest/business-info/locations/archive-location)
   * doesn't affect the location's status.
   * - `INACTIVE` status is currently not supported.
   */
  status?: LocationStatusWithLiterals;
  /**
   * Location type.
   *
   * **Note:** Currently not supported.
   * @deprecated
   */
  locationType?: LocationTypeWithLiterals;
  /** Fax number. */
  fax?: string | null;
  /** Timezone in `America/New_York` format. */
  timeZone?: string | null;
  /** Email address. */
  email?: string | null;
  /** Phone number. */
  phone?: string | null;
  /** Address. */
  address?: Address;
  /**
   * Business schedule. Array of weekly recurring time periods when the location is open for business. Limited to 100 time periods.
   *
   * __Note:__ Not supported by Wix Bookings.
   */
  businessSchedule?: BusinessSchedule;
  /**
   * Revision number, which increments by 1 each time the location is updated.
   * To prevent conflicting changes, the existing revision must be used when updating a location.
   */
  revision?: string | null;
  /**
   * Whether the location is archived. Archived locations can't be updated.
   * __Note:__ [Archiving a location](https://dev.wix.com/api/rest/business-info/locations/archive-location)
   * doesn't affect its `status`.
   * @readonly
   */
  archived?: boolean;
  /**
   * Location types.
   * @maxSize 10
   */
  locationTypes?: LocationTypeWithLiterals[];
  /** Extended fields for data extensions. */
  extendedFields?: ExtendedFields;
}

/** For future use */
export enum LocationStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

/** @enumType */
export type LocationStatusWithLiterals = LocationStatus | 'ACTIVE' | 'INACTIVE';

/** For future use */
export enum LocationType {
  UNKNOWN = 'UNKNOWN',
  BRANCH = 'BRANCH',
  OFFICES = 'OFFICES',
  RECEPTION = 'RECEPTION',
  HEADQUARTERS = 'HEADQUARTERS',
  INVENTORY = 'INVENTORY',
}

/** @enumType */
export type LocationTypeWithLiterals =
  | LocationType
  | 'UNKNOWN'
  | 'BRANCH'
  | 'OFFICES'
  | 'RECEPTION'
  | 'HEADQUARTERS'
  | 'INVENTORY';

export interface Address {
  /**
   * 2-letter country code in an [ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format.
   * @format COUNTRY
   */
  country?: string | null;
  /** Code for a subdivision (such as state, prefecture, or province) in [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2) format. */
  subdivision?: string | null;
  /** City name. */
  city?: string | null;
  /**
   * Postal or zip code.
   * @maxLength 20
   */
  postalCode?: string | null;
  /** Street address. Includes street name, number, and apartment number in separate fields. */
  streetAddress?: StreetAddress;
  /** Full address of the location. */
  formattedAddress?: string | null;
  /**
   * Extra information that helps finding the location.
   * @maxLength 500
   */
  hint?: string | null;
  /** Geographic coordinates of location. */
  geocode?: AddressLocation;
}

/** Street address. Includes street name, number, and apartment number in separate fields. */
export interface StreetAddress {
  /** Street number. */
  number?: string;
  /** Street name. */
  name?: string;
  /** Apartment number. */
  apt?: string;
}

/** Address Geolocation */
export interface AddressLocation {
  /** Latitude of the location. Must be between -90 and 90. */
  latitude?: number | null;
  /** Longitude of the location. Must be between -180 and 180. */
  longitude?: number | null;
}

/** Business schedule. Regular and exceptional time periods when the business is open or the service is available. */
export interface BusinessSchedule {
  /**
   * Weekly recurring time periods when the business is regularly open or the service is available. Limited to 100 time periods.
   * @maxSize 100
   */
  periods?: TimePeriod[];
  /**
   * Exceptions to the business's regular hours. The business can be open or closed during the exception.
   * @maxSize 100
   */
  specialHourPeriod?: SpecialHourPeriod[];
}

/** Weekly recurring time periods when the business is regularly open or the service is available. */
export interface TimePeriod {
  /** Day of the week the period starts on. */
  openDay?: DayOfWeekWithLiterals;
  /**
   * Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are `00:00` to `24:00`, where `24:00` represents
   * midnight at the end of the specified day.
   */
  openTime?: string;
  /** Day of the week the period ends on. */
  closeDay?: DayOfWeekWithLiterals;
  /**
   * Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are `00:00` to `24:00`, where `24:00` represents
   * midnight at the end of the specified day.
   *
   * __Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.
   */
  closeTime?: string;
}

/** Enumerates the days of the week. */
export enum DayOfWeek {
  MONDAY = 'MONDAY',
  TUESDAY = 'TUESDAY',
  WEDNESDAY = 'WEDNESDAY',
  THURSDAY = 'THURSDAY',
  FRIDAY = 'FRIDAY',
  SATURDAY = 'SATURDAY',
  SUNDAY = 'SUNDAY',
}

/** @enumType */
export type DayOfWeekWithLiterals =
  | DayOfWeek
  | 'MONDAY'
  | 'TUESDAY'
  | 'WEDNESDAY'
  | 'THURSDAY'
  | 'FRIDAY'
  | 'SATURDAY'
  | 'SUNDAY';

/** Exception to the business's regular hours. The business can be open or closed during the exception. */
export interface SpecialHourPeriod {
  /** Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time). */
  startDate?: string;
  /** End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time). */
  endDate?: string;
  /**
   * Whether the business is closed (or the service is not available) during the exception.
   *
   * Default: `true`.
   */
  isClosed?: boolean;
  /** Additional info about the exception. For example, "We close earlier on New Year's Eve." */
  comment?: string;
}

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

export interface CreateLocationRequest {
  /** Location to create. */
  location: Location;
}

export interface CreateLocationResponse {
  /** Created location. */
  location?: Location;
}

export interface BulkCreateLocationRequest {
  /**
   * Locations to create.
   * @minSize 1
   * @maxSize 10
   */
  locations: Location[];
}

export interface BulkCreateLocationResponse {
  /** Locations that were created. */
  successfulLocations?: Location[];
  /** Locations that failed. */
  failedLocations?: FailedCreateLocation[];
}

export interface FailedCreateLocation {
  /** Location that couldn't be created. */
  location?: Location;
  /** Error message. */
  errorMessage?: string;
}

export interface GetLocationRequest {
  /**
   * ID of the location to retrieve.
   * @format GUID
   */
  id: string;
}

export interface GetLocationResponse {
  /** Retrieved location. */
  location?: Location;
  /** True if the requesting entity is permitted to manage the location */
  locationManagementAuthorized?: boolean;
}

export interface ListLocationsRequest {
  /** Sort order. */
  sort?: Sorting;
  /**
   * Pagination.
   *
   * Default values:
   * `offset`: 0
   * `limit`: 50 (Max: 1000)
   */
  paging?: Paging;
  /**
   * Whether to include `archived` locations in the response.
   *
   * Default: `false`
   */
  includeArchived?: boolean;
  /** Whether to filter only authorized locations */
  filterAuthorizedLocationEntities?: boolean;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface Paging {
  /** Number of items to load. */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export interface ListLocationsResponse {
  /** Retrieved locations. */
  locations?: Location[];
  /** Paging info. */
  pagingMetadata?: PagingMetadata;
  /**
   * Ids of the locations that the requesting entity can manage
   * @format GUID
   * @maxSize 100
   */
  authorizedLocationEntities?: string[] | null;
}

export interface PagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  offset?: number | null;
  /**
   * Indicates if there are more results after the current page.
   * If `true`, another page of results can be retrieved.
   * If `false`, this is the last page.
   */
  hasNext?: boolean | null;
}

export interface UpdateLocationRequest {
  /** Location to update. */
  location: Location;
}

export interface UpdateLocationResponse {
  /** Updated location. */
  location?: Location;
}

export interface BulkUpdateLocationRequest {
  /**
   * Locations to update.
   * @minSize 1
   * @maxSize 10
   */
  locations?: Location[];
}

export interface BulkUpdateLocationResponse {
  /** Locations that were updated. */
  successfulLocations?: Location[];
  /** Locations that failed. */
  failedLocations?: FailedUpdateLocation[];
}

export interface FailedUpdateLocation {
  /** Location that couldn't be updated. */
  id?: string;
  /** Error message. */
  errorMessage?: string;
}

export interface QueryLocationsRequest {
  /** Information about the filters, sorting, and paging. */
  query?: Query;
  /** Whether to filter only authorized locations */
  filterAuthorizedLocationEntities?: boolean;
}

export interface Query {
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: any;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   */
  sort?: Sorting[];
  /** Paging options to limit and skip the number of items. */
  paging?: Paging;
  /** Array of projected fields. A list of specific field names to return. If `fieldsets` are also specified, the union of `fieldsets` and `fields` is returned. */
  fields?: string[];
  /** Array of named, predefined sets of projected fields. A array of predefined named sets of fields to be returned. Specifying multiple `fieldsets` will return the union of fields from all sets. If `fields` are also specified, the union of `fieldsets` and `fields` is returned. */
  fieldsets?: string[];
}

export interface QueryLocationsResponse {
  /** Retrieved locations. */
  locations?: Location[];
  /** Paging info. */
  pagingMetadata?: PagingMetadata;
  /**
   * Ids of the locations that the requesting entity can manage
   * @format GUID
   * @maxSize 100
   */
  authorizedLocationEntities?: string[] | null;
}

export interface GetOrCreateDefaultLocationRequest {}

export interface GetOrCreateDefaultLocationResponse {
  /** Default location (one that already exists or the one that was created) */
  location?: Location;
}

export interface SetDefaultLocationRequest {
  /**
   * ID of the location to set as the default location.
   * @format GUID
   */
  id: string;
}

export interface SetDefaultLocationResponse {
  /** New default location. */
  location?: Location;
}

export interface SetDefaultLocation {
  /**
   * Previous default location id.
   * @format GUID
   */
  prevDefaultLocationId?: string;
  /**
   * Current default location id.
   * @format GUID
   */
  currDefaultLocationId?: string;
}

export interface ArchiveLocationRequest {
  /**
   * ID of the location to archive.
   * @format GUID
   */
  id: string;
}

export interface ArchiveLocationResponse {
  /** Archived location. */
  location?: Location;
}

export interface ArchiveLocation {
  /** @format GUID */
  id?: string;
}

export interface UnarchiveLocationRequest {
  /**
   * ID of the location to unarchive.
   * @format GUID
   */
  id?: string;
}

export interface UnarchiveLocationResponse {
  /** Unarchived location. */
  location?: Location;
}

export interface AddTypeToLocationRequest {
  /**
   * ID of the location where the type will be added.
   * @format GUID
   */
  id: string;
  /** Location type to add. */
  locationType: LocationTypeWithLiterals;
}

export interface AddTypeToLocationResponse {
  /** Updated location. */
  location?: Location;
}

export interface RemoveTypeFromLocationRequest {
  /**
   * ID of the location where the type will be removed.
   * @format GUID
   */
  id: string;
  /** Location type to remove. */
  locationType: LocationTypeWithLiterals;
}

export interface RemoveTypeFromLocationResponse {
  /** Updated location. */
  location?: Location;
}

export interface MetasiteLocationsRequest {}

export interface MetasiteLocationsResponse {}

export interface AddLocationTypeRequest {
  /**
   * Location id to add type
   * @format GUID
   */
  id: string;
  locationType: LocationTypeWithLiterals;
}

export interface AddLocationTypeResponse {
  /** Full location with the new type */
  location?: Location;
}

export interface RemoveLocationTypeRequest {
  /**
   * Location id to add type
   * @format GUID
   */
  id: string;
  locationType: LocationTypeWithLiterals;
}

export interface RemoveLocationTypeResponse {
  /** Full location with the new type */
  location?: Location;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entityAsJson?: string;
  /** Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity */
  restoreInfo?: RestoreInfo;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntityAsJson?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntityAsJson?: string | null;
}

export interface ActionEvent {
  bodyAsJson?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type CreateLocationApplicationErrors = {
  code?: 'CANNOT_PERFORM_ACTION_ON_ARCHIVED_LOCATION';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type UpdateLocationApplicationErrors =
  | {
      code?: 'CANNOT_PERFORM_ACTION_ON_ARCHIVED_LOCATION';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'CANNOT_ARCHIVE_DEFAULT';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'LOCATION_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type SetDefaultLocationApplicationErrors =
  | {
      code?: 'CANNOT_PERFORM_ACTION_ON_ARCHIVED_LOCATION';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'DEFAULT_NOT_CHANGED';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type ArchiveLocationApplicationErrors = {
  code?: 'CANNOT_ARCHIVE_DEFAULT';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type AddTypeToLocationApplicationErrors = {
  code?: 'CANNOT_UPDATE_LOCATION_TYPES_FOR_LOCATION';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type RemoveTypeFromLocationApplicationErrors = {
  code?: 'CANNOT_UPDATE_LOCATION_TYPES_FOR_LOCATION';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type AddLocationTypeApplicationErrors = {
  code?: 'CANNOT_UPDATE_LOCATION_TYPES_FOR_LOCATION';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type RemoveLocationTypeApplicationErrors = {
  code?: 'CANNOT_UPDATE_LOCATION_TYPES_FOR_LOCATION';
  description?: string;
  data?: Record<string, any>;
};
